import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useForm } from 'antd/es/form/Form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { App } from 'common/components/antd/App';
import { ExtendedTable } from 'common/components/ExtendedTable/ExtendedTable';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { removeEmpty } from 'common/utils/object.utils';
import { useGetAssetsListQuery } from 'domains/assets/queries/assets.query';
import { TAssetEntity, TAssetsListResData } from 'domains/assets/types/assets.types';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListResData, TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import { ECounterpartyType } from 'domains/payments/enums/payments.enums';
import { EReportingPaymentsColumnKeys } from 'domains/reporting/enum/reporting.enum';
import { useGetReportingPaymentsQuery } from 'domains/reporting/queries/reporting.query';
import {
  EReportingPaymentStatus,
  TReportingPaymentsEntity,
  TReportingPaymentsReqParams,
  TReportingPaymentsResData,
} from 'domains/reporting/types/reporting.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { FiltersFormComponent } from './components/filters-form/filters-form.component';
import { EFilterKeys, ReportingPaymentsGetParams } from './enums/payments.enum';
import ReportingPaymentDetailsModal from './modals/details/payments-details.modal';
import { SHeightContainer } from './payment.page.styles';
import { getColumns } from './table/payment-table.columns';
import { TParticipantValue, TPaymentsTableRecord } from './types/payments.types';

const INITIAL_FORM_STATE = {
  [EFilterKeys.GUID]: [''],
  [EFilterKeys.AmountMin]: '',
  [EFilterKeys.AmountMax]: '',
  [EFilterKeys.Asset]: { issuer: '', code: '' },
  min_created_at: null,
  max_created_at: null,
  [EFilterKeys.Status]: [''],
  [EFilterKeys.SenderCounterparty]: '',
  [EFilterKeys.RecipientCounterparty]: '',
  [EFilterKeys.Sender]: [''],
  [EFilterKeys.Receiver]: [''],
  sender_acc_kinds: [''],
  receiver_acc_kinds: [''],
};

type TFormVals = {
  min_amount: string;
  max_amount: string;
  asset: string;
  created_at: [Date | null, Date | null];
  statuses: EReportingPaymentStatus;
  guids: string;
  sender_counterparty: string;
  receiver_counterparty: string;
  sender_ids: TParticipantValue;
  receiver_ids: TParticipantValue;
  sender_acc_kinds: TParticipantValue;
  receiver_acc_kinds: TParticipantValue;
};

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

const addCounterparties = (
  payments: TReportingPaymentsEntity[] | undefined,
  counterparties: TCounterpartyEntity[] | undefined,
): TReportingPaymentsEntity[] =>
  payments
    ? payments.map((payment) => {
        if (!counterparties) return payment;

        const newPayment = payment;

        const senderCty = counterparties.find((cty) => cty.guid === payment.sender_counterparty);
        const recipientCty = counterparties.find((cty) => cty.guid === payment.receiver_counterparty);

        if (senderCty) newPayment.sender_counterparty = senderCty.name;
        if (recipientCty) newPayment.receiver_counterparty = recipientCty.name;

        return newPayment;
      })
    : [];

const ReportingPaymentsPage = (): ReactElement => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentInformationGUID = searchParams.get(ReportingPaymentsGetParams.ModalPaymentInformationGUID);
  const [_filterValues, setFilterValues] = useState<TReportingPaymentsReqParams>(INITIAL_FORM_STATE);
  const filterValues = useMemo(() => removeEmpty(_filterValues), [_filterValues, removeEmpty]);
  const [form] = useForm<TFormVals>();
  const [paginationData, setPaginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [modalData, setModalData] = useState<TPaymentsTableRecord | {}>({});

  const {
    data: paymentsData,
    isFetching: isPaymentsDataLoading,
    isError: isPaymentsDataError,
    error: paymentsDataError,
  } = useGetReportingPaymentsQuery(
    ENV_COUNTERPARTY_GUID,
    {
      ...filterValues,
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
    },
    {
      refetchOnMount: false,
    },
  );

  const {
    data: counterparties,
    isError: isCounterpartiesDataError,
    error: counterpartiesDataError,
  } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
    },
  );

  const {
    data: assets,
    isError: isAssetsDataError,
    error: assetsDataError,
  } = useGetAssetsListQuery(
    ENV_COUNTERPARTY_GUID,
    {},
    {
      refetchOnMount: false,
    },
  );

  const isError = isPaymentsDataError || isCounterpartiesDataError || counterpartiesDataError;
  const errorData = paymentsDataError || isAssetsDataError || assetsDataError;

  useEffect(() => {
    if (isError) {
      notification.error({
        message: (errorData as { message: string }).message,
        type: 'error',
      });
    }
  }, [isError, errorData]);

  const paymentsWithCounterparties = addCounterparties(paymentsData?.payments, counterparties?.counterparties);

  const onCellClickHandler = (record: TReportingPaymentsEntity, cellKey: string): void => {
    if (cellKey === EReportingPaymentsColumnKeys.Details) {
      setModalData(record);
    }
  };

  const columns = getColumns({ navigate, onCellClickHandler });

  const handleCounterpartiesPaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const accKindsFormat = ({ type }: TParticipantValue): string[] => {
    if (type === ECounterpartyType.Bank) {
      return ['processing', 'emission'];
    }
    if (type === ECounterpartyType.Customer) {
      return ['customer'];
    }

    return [''];
  }

  const guidCheck = ({ guid }: TParticipantValue): string[] => {
    if (!guid) {
      return [''];
    }

    return [guid];
  }

  const defaultParticipantValue = {
    guid: '',
    type: '',
    walletUserExternalId: '',
  }

  const onSubmit = (values: TFormVals): void => {
    let valuesFromForm = {
      [EFilterKeys.GUID]: [values.guids],
      [EFilterKeys.AmountMin]: values.min_amount,
      [EFilterKeys.AmountMax]: values.max_amount,
      [EFilterKeys.Status]: [values.statuses],
      [EFilterKeys.SenderCounterparty]: values.sender_counterparty,
      [EFilterKeys.RecipientCounterparty]: values.receiver_counterparty,
      [EFilterKeys.Sender]: guidCheck(values.sender_ids ?? defaultParticipantValue),
      [EFilterKeys.Receiver]: guidCheck(values.receiver_ids ?? defaultParticipantValue),
      sender_acc_kinds: accKindsFormat(values.sender_ids ?? defaultParticipantValue),
      receiver_acc_kinds: accKindsFormat(values.receiver_ids ?? defaultParticipantValue),
    } as TReportingPaymentsReqParams;

    if (values.asset) {
      const parsedAsset = JSON.parse(values.asset) as TAssetEntity;
      if (parsedAsset) {
        valuesFromForm = {
          ...valuesFromForm,
          asset_codes: [parsedAsset.asset_code],
          asset_issuers: [parsedAsset.asset_issuer],
        };
      }
    }

    if (values.created_at) {
      if (values.created_at[0]) {
        const minCreatedAt = new Date(values.created_at[0]);
        minCreatedAt.setUTCHours(0, 0, 0, 0);
        const minOffsetMs = minCreatedAt.getTimezoneOffset() * 60 * 1000;
        const minLocalTime = new Date(minCreatedAt.getTime() - minOffsetMs);
        valuesFromForm.min_created_at = minLocalTime.toISOString();
      }
      if (values.created_at[1]) {
        const maxCreatedAt = new Date(values.created_at[1]);
        maxCreatedAt.setUTCHours(23, 59, 59, 999);
        const maxOffsetMs = maxCreatedAt.getTimezoneOffset() * 60 * 1000;
        const maxLocalTime = new Date(maxCreatedAt.getTime() - maxOffsetMs);
        valuesFromForm.max_created_at = maxLocalTime.toISOString();
      }
    }

    const valuesToSend = removeEmpty(valuesFromForm);
    setPaginationData((currentPaginationData) => ({
      page: DEFAULT_PAGE,
      pageSize: currentPaginationData.pageSize,
    }));

    if (valuesFromForm?.guids !== undefined && valuesFromForm?.guids[0]?.length) {
      setFilterValues({
        guids: [valuesFromForm?.guids[0]]
      });
    } else {
      setFilterValues(valuesToSend);
    }
  };

  const onCancelModalHandler = (): void => {
    navigate(ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS);
  };

  const onClearFormHandler = (): void => {
    setFilterValues({});
  }

  return (
    <SHeightContainer>
      <FiltersFormComponent
        onSubmit={onSubmit}
        onClear={onClearFormHandler}
        form={form}
        counterparties={counterparties?.counterparties || []}
        assets={assets?.stellar_assets || []}
        isLoading={isPaymentsDataLoading}
      />
      <ExtendedTable
        loading={isPaymentsDataLoading}
        rootClassName="reporting-table"
        pagination={{
          style: {
            marginRight: 30,
          },
          showQuickJumper: true,
          showSizeChanger: true,
          total: paymentsData?.total,
          pageSize: paginationData.pageSize,
          current: paginationData.page,
          onChange: handleCounterpartiesPaginationChange,
        }}
        rowKey={EReportingPaymentsColumnKeys.Guid}
        dataSource={paymentsWithCounterparties as TPaymentsTableRecord[]}
        columns={columns}
        hideFiltersBtn
      />
      <ReportingPaymentDetailsModal
        isOpen={!!paymentInformationGUID}
        onCancelModalHandler={onCancelModalHandler}
        data={modalData}
      />
    </SHeightContainer>
  );
};

export const loader = async (): Promise<{
  paymentsData: TReportingPaymentsResData | {},
  counterpartiesData: TCounterpartiesListResData | {},
  assetsData: TAssetsListResData | {},
}> => {
  try {
    const paymentsQuery = queryKeysClient[EQueryKeys.ReportingPaymentsQueryKeys].getPayments_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
      },
    );

    const paymentsData: TReportingPaymentsResData =
      queryClient.getQueryData(paymentsQuery.queryKey) ??
      (await queryClient.fetchQuery(paymentsQuery));

    const counterpartiesQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesQuery));

    const assetsQuery = queryKeysClient[EQueryKeys.AssetQueryKeys].getAssetsList_MultiRole(ENV_COUNTERPARTY_GUID, {});
    const assetsData: TAssetsListResData =
      queryClient.getQueryData(assetsQuery.queryKey) ??
      (await queryClient.fetchQuery(assetsQuery));

    return {
      paymentsData,
      counterpartiesData,
      assetsData,
    };
  } catch (error) {
    console.error(error);

    return {
      paymentsData: {},
      counterpartiesData: {},
      assetsData: {},
    };
  }
};

export default ReportingPaymentsPage;
